import { OAuthStatusResponse } from '@firestore-gui/shared';
import { Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';
import { useGoogleCloudConnectionBanner } from './use-google-cloud-connection-banner';

interface GoogleCloudConnectionBannerProps {
  oauthStatus: OAuthStatusResponse | null;
  onStatusChange: () => Promise<void>;
}

export function GoogleCloudConnectionBanner({
  oauthStatus,
  onStatusChange,
}: GoogleCloudConnectionBannerProps) {
  const f = useGoogleCloudConnectionBanner({ oauthStatus, onStatusChange });

  if (!f.shouldShow) return null;

  return (
    <div className="w-full flex justify-center mb-6 px-4 sm:px-6">
      <Card className="w-full max-w-4xl border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">Connect Google Cloud</h3>
              <p className="text-sm text-muted-foreground">
                Connect your Google Cloud account to browse Firestore databases and manage your
                data.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Button
                onClick={f.handleConnect}
                disabled={f.isConnecting}
                size="lg"
                className="w-full sm:w-auto text-base px-8 py-3 h-auto"
              >
                {f.isConnecting ? 'Connecting...' : 'Connect Google Cloud'}
              </Button>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span>What access will be requested</span>
            </h4>

            <p className="text-sm text-muted-foreground">
              We'll request access to list your GCP projects and manage Firestore data you
              explicitly interact with. No background access, no hidden writes, and credentials are
              deleted when you choose to disconnect.
            </p>

            <ul className="space-y-2 text-sm text-muted-foreground list-none pl-0">
              <li className="flex items-start gap-2">
                <span className="font-medium text-foreground">•</span>
                <span>
                  <strong className="font-medium text-foreground">
                    View Google Cloud project metadata
                  </strong>
                  <br />
                  Required to list your GCP projects, show available Firestore databases, and
                  display the Google account you're connected with.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-foreground">•</span>
                <span>
                  <strong className="font-medium text-foreground">
                    Manage Firestore / Datastore data
                  </strong>
                  <br />
                  Required to list databases, browse collections, run queries, and edit documents
                  you explicitly interact with in the Explorer.
                </span>
              </li>
            </ul>

            <p className="text-sm text-muted-foreground mt-4">
              You can revoke access at any time, and clicking{' '}
              <strong>Disconnect & Sign Out</strong> removes stored connection credentials and the
              active Firestore selection immediately.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
